import { Empty, Typography } from "antd";
import { useUtilities } from "contexts/UtilitiesContext";
import React, { memo, useCallback } from "react";
import { AnyaSessionMode } from "types/anya";
import AnyaAgentsState from "views/Automations/AnyaAgents/components/AnyaAgentsState";
import AnyaAgentSwitcher from "views/Dashboards/AnyaAgentSwitcher";
import "views/Dashboards/AnyaProse.less";
import "views/Dashboards/AnyaStatusText.less";
import StreamingText from "views/Dashboards/StreamingText";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACButton from "ac-components/ac-button/ACButton";
import AnyaChatLoadingIcon from "ac-components/ac-icons/AnyaChatLoadingIcon";
import AnyaIcon from "ac-components/ac-icons/AnyaIcon";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import RefreshIcon from "ac-components/ac-icons/RefreshIcon";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";

import { useFindingDetails } from "../FindingDetailsProvider";
import useFindingAnyaAgentsTab from "./useFindingAnyaAgentsTab";

const AGENTS_BUTTON_WIDTH = 150;

const AnyaAgentsTab = memo(function AnyaAgentsTab() {
  const { id, existingConversations } = useFindingDetails();
  const { showAnyaSummaryPopup } = useUtilities();
  const {
    agentsLoading,
    agentsError,
    agentOptions,
    searchAgents,
    selectedAgent,
    selectAgent,
    agentResponse,
    agentResponseLoading,
    agentResponseError,
    agentResponseExists,
    retryAgentResponse,
  } = useFindingAnyaAgentsTab(id);

  const openAskAnya = useCallback(() => {
    if (!id || !selectedAgent) {
      return;
    }

    showAnyaSummaryPopup({
      dashboardId: id,
      conversationId: agentResponse?.conversationId,
      config: {
        widgetType: String(id),
        existingConversations,
      },
      initialSessionMode: AnyaSessionMode.AGENT,
      initialSelectedAgent: selectedAgent,
      startWithEmptyPrompt: true,
    });
  }, [
    agentResponse?.conversationId,
    existingConversations,
    id,
    selectedAgent,
    showAnyaSummaryPopup,
  ]);

  const selector = (
    <div className="d-flex-inline align-items-center w-100">
      <AnyaAgentSwitcher
        selectedAgent={selectedAgent}
        selectedMode={AnyaSessionMode.AGENT}
        agentOptions={agentOptions}
        searchAgents={searchAgents}
        onSelectAgent={selectAgent}
        menuWidth={AGENTS_BUTTON_WIDTH + 100}
        itemTextWidth={AGENTS_BUTTON_WIDTH + 50}
      >
        {() => (
          <ACButton
            type="outline"
            rightIcon={<ChevronDownIcon className="color-supporting" />}
            className="text-left"
          >
            <div className="d-flex">
              <span className="text-bold">Agent:</span>
              <span className="m-l-xs" style={{ width: AGENTS_BUTTON_WIDTH }}>
                <ACMiddleEllipsis
                  text={selectedAgent?.name || "Select agent"}
                  applyWidth
                />
              </span>
            </div>
          </ACButton>
        )}
      </AnyaAgentSwitcher>
      <ACButtonGroup size="md" className="m-l-auto">
        <ACButtonGroupItem
          disabled={!selectedAgent || agentsLoading || agentResponseLoading}
          onClick={openAskAnya}
        >
          <span className="d-flex align-items-center justify-content-center">
            <AnyaIcon /> <span className="m-l-xs">Ask Anya</span>
          </span>
        </ACButtonGroupItem>
        <ACButtonGroupItem
          aria-label="Refresh chat"
          title="Refresh chat"
          disabled={!selectedAgent || agentsLoading || agentResponseLoading}
          onClick={retryAgentResponse}
        >
          <span className="d-flex align-items-center justify-content-center">
            <RefreshIcon />
          </span>
        </ACButtonGroupItem>
      </ACButtonGroup>
    </div>
  );

  let content = null;

  if (agentsLoading && !selectedAgent) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Typography.Text className="system-process">
          Loading Anya agents <AnyaChatLoadingIcon />
        </Typography.Text>
      </div>
    );
  } else if (agentsError && !agentOptions.length) {
    content = (
      <AnyaAgentsState
        title="Unable to load agents"
        description={agentsError}
        variant="error"
      />
    );
  } else if (!selectedAgent) {
    content = (
      <AnyaAgentsState
        title="No default agent available"
        description="Search and select an agent to load finding-specific output."
        icon={<AnyaIcon className="font-size-32 color-light" />}
        variant="muted"
      />
    );
  } else if (agentResponseLoading) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Typography.Text className="system-process">
          Loading agent output <AnyaChatLoadingIcon />
        </Typography.Text>
      </div>
    );
  } else if (agentResponseError) {
    content = (
      <AnyaAgentsState
        title="Unable to load agent output"
        description={agentResponseError}
        actions={
          <ACButton type="ghost" size="sm" onClick={retryAgentResponse}>
            Retry
          </ACButton>
        }
        variant="error"
      />
    );
  } else if (!agentResponseExists) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Empty description="No output available for this agent" />
      </div>
    );
  } else if (!agentResponse?.reasoningSummary && !agentResponse?.details) {
    content = (
      <div className="d-flex flex-column align-items-center justify-content-center text-center">
        <Empty description="Select an agent to view finding insights" />
      </div>
    );
  } else {
    content = (
      <div className="anya-prose">
        <div className="w-100">
          {agentResponse?.details ? (
            <StreamingText
              text={agentResponse.details}
              stream={false}
              onComplete={() => {}}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="p-xs">
      {selector}
      {content}
    </div>
  );
});

export default AnyaAgentsTab;
